import { Viewer3D } from '@components'
import { ResponsiveShell } from '@components/ResponsiveShell/export'
import AnimationControl from '@components/DirectionControls/AnimationControl'
// import { FPSIndicator } from '@components/Topbar/FPSIndicator'
import { KUBE_SCENE_LIGHT_MIN_HEIGHT, MODE_3D_VIEW } from '@constants'
import { ViewersContextProvider } from '@context/viewers'
import { useModeShortcuts } from '@hooks/useModeShortcuts'
import { useRollback } from '@hooks/useRollback'
import { useViewportLayout } from '@hooks/useViewportLayout'
import { establishHistoryBaseline, runWithoutHistoryRecording } from '@utils/historyRecording'
import { refreshAllTextureAnisotropy } from '@utils/sceneObjects/textureCache'
import { useAppStore, useHistoryMetaStore } from '@store'
import { Canvas } from '@react-three/fiber'
import {
  consumePointerMissedSuppression,
  shouldSuppressClickAfterDrag,
} from '@components/Viewer3D/pointerSelectionGuard'
import CameraControls from 'camera-controls'
import { useEffect, useRef, useState } from 'react'
import { SnapDebugHud } from '@components/Viewer3D/debug/ModularSnapDebug3D'
import { ModularSectionToolbarOverlay } from '@components/Viewer3D/linked/ModularSectionToolbarOverlay'

/** Correspondencia: ATEMPORAL mobile-28-07-2026 (`47ee2081`) — CanvasHost + ResponsiveShell. */
export function ViewerManager() {
  useRollback()
  useModeShortcuts()
  const layout = useViewportLayout()
  const isTouchChrome = layout !== 'desktop'
  const [controls, setControls] = useState<typeof CameraControls | undefined>()
  const currentObjectID = useAppStore.use.currentObjectID()
  const unselectAll = useAppStore.use.unselectAll()
  const setCurrentObjectID = useAppStore.use.setCurrentObjectID()
  const isModalOpen = useAppStore.use.isModalOpen()
  const frameloop = useAppStore((state) => state.frameloop)
  const performanceMode = useAppStore((s) => s.performanceMode)
  const glRef = useRef<any>(null)
  const mode = useAppStore((s) => s.mode)
  const setMode = useAppStore.use.setMode()

  // Asegurar que siempre estemos en modo 3D
  useEffect(() => {
    if (
      mode !== MODE_3D_VIEW &&
      mode !== 'MODE_ALZADOS' &&
      mode !== 'MODE_FORCE_ALZADOS' &&
      mode !== 'MODE_DRAGGING_ITEM_3D'
    ) {
      setMode(MODE_3D_VIEW)
    }
  }, [mode, setMode])

  const prefs = useAppStore.use.prefs()
  const items = useAppStore.use.items()
  const currentLayer = useAppStore.use.currentLayer()
  const VERTECHO = prefs?.VERTECHO
  const bg = 0xffffff

  const prevVertechoRef = useRef<boolean | undefined>(VERTECHO)
  const historyBaselineEstablishedRef = useRef(false)

  useEffect(() => {
    historyBaselineEstablishedRef.current = false
  }, [prefs])

  useEffect(() => {
    if (!prefs || Object.keys(prefs).length === 0 || historyBaselineEstablishedRef.current) return

    const timer = window.setTimeout(() => {
      const { canUndo, canRedo } = useHistoryMetaStore.getState()
      if (!canUndo && !canRedo) {
        establishHistoryBaseline()
      }
      historyBaselineEstablishedRef.current = true
    }, 1200)

    return () => window.clearTimeout(timer)
  }, [prefs])

  useEffect(() => {
    if (VERTECHO === undefined || prevVertechoRef.current === VERTECHO) {
      prevVertechoRef.current = VERTECHO
      return
    }

    runWithoutHistoryRecording(() => {
      useAppStore.setState((store) => {
        store.areas.forEach((area) => {
          if (area.isRoof && area.visible !== VERTECHO) {
            area.visible = VERTECHO
          }
        })
      })
    })

    prevVertechoRef.current = VERTECHO
  }, [VERTECHO])

  useEffect(() => {
    if (VERTECHO === undefined) return

    runWithoutHistoryRecording(() => {
      useAppStore.setState((store) => {
        let needsSync = false
        store.areas.forEach((area) => {
          if (area.isRoof && area.visible !== VERTECHO) {
            needsSync = true
          }
        })

        if (needsSync) {
          store.areas.forEach((area) => {
            if (area.isRoof) {
              area.visible = VERTECHO
            }
          })
        }
      })
    })
  }, [])

  // Asegurar que las luces base existan siempre al cargar/refrescar la página
  // Verificar y crear las luces cuando cambien los items o las preferencias
  useEffect(() => {
    // Solo crear si las preferencias están disponibles
    if (!prefs || Object.keys(prefs).length === 0) {
      return
    }

    const store = useAppStore.getState()
    const defaultHeight = KUBE_SCENE_LIGHT_MIN_HEIGHT

    // Intentar crear las luces con un pequeño delay para asegurar que los recursos estén listos
    const timer = setTimeout(() => {
      // Crear lights_baseSceneLight si no existe
      if (
        !items.has('lights_baseSceneLight') ||
        !store.layers[currentLayer].items.has('lights_baseSceneLight')
      ) {
        store
          .drawLight('lights_baseSceneLight', '', 'LUZGENERAL', false, defaultHeight)
          .catch((err) => {
            // Reintentar después de un delay si falla
            setTimeout(() => {
              const storeRetry = useAppStore.getState()
              if (!storeRetry.items.has('lights_baseSceneLight')) {
                storeRetry
                  .drawLight('lights_baseSceneLight', '', 'LUZGENERAL', false, defaultHeight)
                  .catch(() => {
                    // Error silencioso en reintento
                  })
              }
            }, 2000)
          })
      }

      // Crear lights_baseAreaLight si no existe
      if (
        !items.has('lights_baseAreaLight') ||
        !store.layers[currentLayer].items.has('lights_baseAreaLight')
      ) {
        store
          .drawLight('lights_baseAreaLight', '', 'Luzgeneral2', false, defaultHeight)
          .catch((err) => {
            // Reintentar después de un delay si falla
            setTimeout(() => {
              const storeRetry = useAppStore.getState()
              if (!storeRetry.items.has('lights_baseAreaLight')) {
                storeRetry
                  .drawLight('lights_baseAreaLight', '', 'Luzgeneral2', false, defaultHeight)
                  .catch(() => {
                    // Error silencioso en reintento
                  })
              }
            }, 2000)
          })
      }
    }, 500) // Pequeño delay para asegurar que los recursos estén listos

    return () => clearTimeout(timer)
  }, [items, prefs, currentLayer])

  return (
    <div className={`relative flex h-screen w-screen flex-col ${bg}`}>
      <ViewersContextProvider value={{ controls, setControls }}>
        {/*
          CanvasHost estable.
          Desktop: top-20 (bajo Topbar h-20), bottom 0 — viewport histórico.
          Touch: top-12 (CompactTopbar) + bottom-14 (ModeBar) — máximo canvas útil.
        */}
        <div
          className={
            isTouchChrome
              ? 'absolute bottom-14 left-0 right-0 top-12 z-0'
              : 'absolute bottom-0 left-0 right-0 top-20 z-0'
          }
        >
          <Canvas
            shadows={!performanceMode}
            frameloop={frameloop}
            style={{ width: '100%', height: '100%' }}
            onPointerMissed={() => {
              if (isModalOpen) return
              const currentMode = useAppStore.getState().mode
              if (currentMode === 'MODE_DRAGGING_ITEM_3D' || currentMode === 'MODE_DRAGGING_ITEM') {
                return
              }
              if (consumePointerMissedSuppression()) {
                return
              }
              if (shouldSuppressClickAfterDrag()) {
                return
              }
              unselectAll()
              setCurrentObjectID('')
            }}
            //TODO:SOlo se debería preserveDrawingBuffer:true cuando se hace una captura del canvas, sino se gasta más memoria de la necesaria, lo mismo con el alpha!!
            gl={{
              preserveDrawingBuffer: true,
              alpha: true,
              antialias: !performanceMode,
              powerPreference: 'high-performance',
            }}
            onCreated={({ gl, scene }) => {
              glRef.current = gl
              ;(globalThis as { __maxTextureAnisotropy?: number }).__maxTextureAnisotropy =
                gl.capabilities.getMaxAnisotropy()
              refreshAllTextureAnisotropy(scene)
            }}
            ref={(ref) => {
              if (ref && ref.gl) glRef.current = ref.gl
            }}
          >
            <AnimationControl />
            <Viewer3D />
          </Canvas>
        </div>
        <div className='relative z-[1] flex min-h-0 w-full flex-col'>
          <ResponsiveShell />
          <ModularSectionToolbarOverlay />
        </div>
        <SnapDebugHud />
        {/* <FPSIndicator /> */}
      </ViewersContextProvider>
    </div>
  )
}
